import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { storeAuthData } from '@/utils/auth';
import { landingAfterSignIn } from '@/utils/todays-tips-ready';
import { trackMetaPurchase } from '@/utils/facebook-pixel-events';
import { PRODUCT_LEXI_PREMIUM } from '@/utils/vars';

/**
 * StripePaymentLinkRedirect - Handles redirect after Stripe payment link checkout
 * Shows thank you message and verification status
 */
export default function StripePaymentLinkRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('verifying');
  const sessionId = searchParams.get('session_id');
  const [verificationAttempt, setVerificationAttempt] = useState(0);

  useEffect(() => {
    // Verify Stripe session and get user details
    const verifyPayment = async () => {
      if (!sessionId) {
        console.error('No session ID provided');
        setVerificationStatus('error');
        return;
      }

      try {
        // Call the API to verify the Stripe session
        const response = await fetch('/api/verify-stripe-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            checkoutSessionId: sessionId
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({
            error: 'Failed to verify session'
          }));
          console.error('Verification failed:', errorData);
          setVerificationStatus('error');
          return;
        }

        const data = await response.json();
        console.log('Verification response:', data);

        if (data.success) {
          // Meta Purchase for this payment, deduplicated on the Checkout Session ID
          if (data.purchase) {
            trackMetaPurchase({
              value: data.purchase.value,
              currency: data.purchase.currency,
              productName: data.purchase.productName || PRODUCT_LEXI_PREMIUM,
              eventId: data.purchase.id || sessionId,
            });
          }

          // Store the verification data in sessionStorage for the verification page
          if (data.sid) {
            sessionStorage.setItem('verificationSid', data.sid);
          }
          if (data.email) {
            sessionStorage.setItem('tempEmail', data.email);
          }
          if (data.phone) {
            sessionStorage.setItem('cleanPhoneNumber', data.phone);
            sessionStorage.setItem('phoneNumber', data.phone);
          }

          setVerificationStatus('verified');
          
          // If we already have a token (auto-login case), store everything and go to todays-tips
          if (data.token) {
            const verificationSid = data.sid || 'stripe-verified';
            storeAuthData(verificationSid, data.email, data.phone, data.token, 72);
            
            if (data.email) {
              sessionStorage.setItem('userEmail', data.email);
              localStorage.setItem('userEmail', data.email);
            }
            
            setTimeout(async () => {
              // Check onboarding status from sessionStorage (extracted from token)
              const onboarding = sessionStorage.getItem("onboarding");
              const needsOnboarding = onboarding === "true";

              // Check if we have a redirect path
              const redirectPath = sessionStorage.getItem("redirectAfterLogin");

              if (needsOnboarding) {
                window.location.href = "/onboard";
              } else if (redirectPath) {
                // Clear the redirect path from session storage
                sessionStorage.removeItem("redirectAfterLogin");
                // /home instead of /todays-tips until today's tips are out
                window.location.href = await landingAfterSignIn(redirectPath);
              } else {
                // Default redirect to today's tips page, or /home until today's tips are out
                window.location.href = await landingAfterSignIn("/todays-tips");
              }
            }, 2000);
          } else {
            // No token yet, redirect to login page
            setTimeout(() => {
              window.location.href = "/login";
            }, 2000);
          }
        } else {
          console.error('Verification was not successful:', data);
          setVerificationStatus('error');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setVerificationStatus('error');
      }
    };

    setVerificationStatus('verifying');
    verifyPayment();
  }, [navigate, sessionId, verificationAttempt]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {verificationStatus === 'verifying' && (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 className="h-16 w-16 text-brand-pink animate-spin" />
            </div>
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Thank You for Purchasing!
            </h2>
            <p className="text-gray-600 mb-2">
              We&apos;re verifying your payment...
            </p>
            <p className="text-sm text-gray-500">
              Please wait while we confirm your subscription.
            </p>
          </>
        )}

        {verificationStatus === 'verified' && (
          <>
            <div className="flex justify-center mb-6">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-semibold mb-4 text-green-700">
              Payment Verified!
            </h2>
            <p className="text-gray-600 mb-2">
              Your subscription has been confirmed.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Redirecting you to complete your setup...
            </p>
            <div className="flex justify-center">
              <Loader2 className="h-6 w-6 text-brand-pink animate-spin" />
            </div>
          </>
        )}

        {verificationStatus === 'error' && (
          <>
            <div className="rounded-full h-12 w-12 bg-red-100 flex items-center justify-center mx-auto mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2 text-red-600">Verification Issue</h2>
            <p className="text-gray-600 mb-4">
              There was an issue verifying your payment.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Retry verification without making another payment.
            </p>
            <button
              onClick={() => setVerificationAttempt(attempt => attempt + 1)}
              className="w-full mb-3 bg-brand-pink text-white px-6 py-2 rounded-lg font-semibold"
            >
              Retry verification
            </button>
            <button
              onClick={() => window.location.href = "/login"}
              className="w-full bg-brand-pink text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors"
            >
              Go to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}

